import { useRef, useCallback } from 'react';
import Swal from 'sweetalert2';

export interface AdminImageUploadProps {
  label: string;
  currentImage: string;
  onChange: (file: File) => void;
  onRemove: () => void;
  acceptedTypes?: string[];
  maxSizeMB?: number;
  recommendedDimensions?: string;
  disabled?: boolean;
  required?: boolean;
  previewHeight?: string;
  previewWidth?: string;
  placeholderIcon?: string;
}

const DEFAULT_ACCEPTED = ['image/png', 'image/jpeg', 'image/webp'];
const DEFAULT_MAX_MB = 5;

const formatAcceptedLabel = (types: string[]): string =>
  types.map((t) => t.replace('image/', '').toUpperCase()).join(', ');

const formatFileSize = (mb: number): string =>
  mb >= 1 ? `${mb} MB` : `${(mb * 1024).toFixed(0)} KB`;

export default function AdminImageUpload({
  label,
  currentImage,
  onChange,
  onRemove,
  acceptedTypes = DEFAULT_ACCEPTED,
  maxSizeMB = DEFAULT_MAX_MB,
  recommendedDimensions,
  disabled = false,
  required = false,
  previewHeight = 'h-40',
  previewWidth = 'w-full',
  placeholderIcon = 'ri-image-add-line',
}: AdminImageUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const acceptedString = acceptedTypes.join(',');

  const validateFile = useCallback(
    (file: File): string | null => {
      if (!acceptedTypes.includes(file.type)) {
        return `Formato no soportado. Usá ${formatAcceptedLabel(acceptedTypes)}.`;
      }
      if (file.size > maxSizeMB * 1024 * 1024) {
        return `El archivo supera los ${formatFileSize(maxSizeMB)}.`;
      }
      return null;
    },
    [acceptedTypes, maxSizeMB],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const error = validateFile(file);
      if (error) {
        Swal.fire({
          title: 'Archivo no válido',
          text: error,
          icon: 'error',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#ef4444',
          customClass: {
            popup: 'rounded-2xl',
            title: 'font-bold text-sm',
            confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
            htmlContainer: 'text-sm',
          },
          buttonsStyling: false,
        });
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      onChange(file);
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [onChange, validateFile],
  );

  const handleRemove = useCallback(() => {
    Swal.fire({
      title: '¿Eliminar imagen?',
      text: 'La imagen se quitará del campo. Podés volver a subir una después.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold text-sm',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        htmlContainer: 'text-sm',
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        onRemove();
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    });
  }, [onRemove]);

  const triggerFileInput = () => {
    if (!disabled) fileInputRef.current?.click();
  };

  const hasImage = currentImage && currentImage.length > 0;

  return (
    <div>
      <label className="block text-[11px] font-bold text-foreground-600 uppercase tracking-wider mb-1.5">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>

      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedString}
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />

      {/* Preview Area */}
      {hasImage ? (
        <div className="relative rounded-xl overflow-hidden border border-background-200 bg-background-100">
          <img
            src={currentImage}
            alt={label}
            className={`${previewWidth} ${previewHeight} object-cover object-top`}
          />
          {/* Action overlay */}
          <div className="absolute top-2 right-2 flex items-center gap-1.5">
            <button
              type="button"
              onClick={triggerFileInput}
              disabled={disabled}
              className="w-8 h-8 rounded-lg bg-foreground-950/60 hover:bg-foreground-950/80 flex items-center justify-center cursor-pointer transition-colors backdrop-blur-sm disabled:opacity-40 disabled:cursor-not-allowed"
              title="Reemplazar imagen"
            >
              <i className="ri-image-edit-line text-sm text-background-50"></i>
            </button>
            <button
              type="button"
              onClick={handleRemove}
              disabled={disabled}
              className="w-8 h-8 rounded-lg bg-foreground-950/60 hover:bg-red-600 flex items-center justify-center cursor-pointer transition-colors backdrop-blur-sm disabled:opacity-40 disabled:cursor-not-allowed"
              title="Eliminar imagen"
            >
              <i className="ri-delete-bin-line text-sm text-background-50"></i>
            </button>
          </div>
          {/* Info bar */}
          <div className="px-3 py-2 flex items-center gap-2 bg-background-50">
            <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
              <i className="ri-check-line text-emerald-600 text-xs font-bold"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold text-foreground-800 truncate">
                Imagen cargada
              </p>
              <p className="text-[10px] text-foreground-400">
                {recommendedDimensions ? `${recommendedDimensions} · ` : ''}
                {formatAcceptedLabel(acceptedTypes)} · Máx {formatFileSize(maxSizeMB)}
              </p>
            </div>
          </div>
        </div>
      ) : (
        /* Upload zone */
        <div
          onClick={triggerFileInput}
          className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all group ${
            disabled
              ? 'border-background-200 bg-background-100/50 cursor-not-allowed opacity-60'
              : 'border-background-300 hover:border-primary-300 hover:bg-background-100'
          }`}
        >
          <div className="w-12 h-12 mx-auto rounded-2xl bg-background-200 flex items-center justify-center group-hover:bg-primary-100 group-hover:scale-110 transition-all duration-200">
            <i className={`${placeholderIcon} text-xl text-foreground-400 group-hover:text-primary-500 transition-colors`}></i>
          </div>
          <p className="mt-3 text-sm font-semibold text-foreground-700">
            {disabled ? 'No disponible' : 'Hacé clic para subir una imagen'}
          </p>
          <p className="mt-1 text-[11px] text-foreground-400">
            {formatAcceptedLabel(acceptedTypes)} · Máx {formatFileSize(maxSizeMB)}
            {recommendedDimensions ? ` · ${recommendedDimensions}` : ''}
          </p>
          {!disabled && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); triggerFileInput(); }}
              className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-background-200 text-foreground-600 text-xs font-semibold hover:bg-background-300 cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-upload-cloud-2-line text-sm"></i>
              Upload Image
            </button>
          )}
        </div>
      )}
    </div>
  );
}